import { ColumnFilterValues, GameDataRefDto } from './game-data.types';

/**
 * Стили персонажа (орнаменты) — первая таблица игровых данных.
 * DTO повторяют Swagger (`/admin/game-data/ornaments`) один в один.
 */

/** Код категории стилей. Подпись категории берётся с бэка (`title`), НЕ отсюда. */
export type OrnamentCategoryCode =
  | 'APPEARANCE_WEAPON'
  | 'KILL_EFFECT'
  | 'CHAT_BACKGROUND'
  | 'APPEARANCE_ARMOR'
  | 'APPEARANCE_HAT'
  | 'APPEARANCE_CLOAK'
  | 'APPEARANCE_AGATHION'
  | 'APPEARANCE_MOUNT'
  | 'APPEARANCE_PET'
  | 'HIT_EFFECT'
  | 'APPEARANCE_SHIELD'
  | 'IMPACT_EFFECT';

/**
 * Поля, применимость которых зависит от категории.
 * Набор для каждой категории приходит в `OrnamentCategoryDto.fields` — матрицу
 * на фронте НЕ зашивать, поля вне списка прятать.
 */
export type OrnamentField =
  | 'SWAP_ITEM_ID'
  | 'NPC_ID'
  | 'TRANSFORMATION_ID'
  | 'SKILL_ID'
  | 'HIT_EFFECT'
  | 'ACTIVATABLE_ITEMS'
  | 'SKILL_EFFECT'
  | 'CHAT_BACKGROUND'
  | 'LIMITED_ICON';

/** Поля, которые массовая правка умеет очищать. */
export type OrnamentClearField =
  | 'MARK_TYPE'
  | 'SKILL_EFFECT'
  | 'CHAT_BACKGROUND'
  | 'LIMITED_ICON'
  | 'ACTIVATION_COST'
  | 'DEACTIVATION_COST'
  | 'ACTIVATABLE_ITEMS';

export type OrnamentSortOption =
  | 'style'
  | 'style_desc'
  | 'name'
  | 'name_desc'
  | 'updated'
  | 'updated_desc';

/**
 * Цена активации или деактивации стиля: предмет + количество.
 *
 * `item` приходит только на чтение — на запись уходят ровно `itemId` и `count`
 * (за этим следит `buildOrnamentPayload`).
 */
export interface OrnamentCostDto {
  itemId: number;
  count: number;
  item?: GameDataRefDto | null;
}

/** Ограничение «кому доступен стиль»: раса, пол, класс. */
export interface OrnamentActivatableItemDto {
  raceId: number;
  sexId: number;
  classId: number;
}

/** Стиль персонажа. */
export interface OrnamentDto {
  id: number;
  /** Версия строки. Её обязательно вернуть в PUT, иначе правка отклоняется как устаревшая. */
  version: number;
  categoryId: number;
  categoryCode: OrnamentCategoryCode;
  /** Подпись категории на языке запроса — брать её, а не хардкод. */
  categoryTitle: string;
  gameCategoryId: number;
  styleId: number;
  name: string;
  swapItemId?: number | null;
  /** Расшифровка `swapItemId`: название и иконка. Только на чтение. */
  swapItem?: GameDataRefDto | null;
  npcId?: number | null;
  /** Расшифровка `npcId`. Только на чтение. */
  npc?: GameDataRefDto | null;
  transformationId?: number | null;
  skillId?: number | null;
  /** Расшифровка `skillId`. Только на чтение. */
  skill?: GameDataRefDto | null;
  hitEffectChargeSkillId?: number | null;
  /** Расшифровка `hitEffectChargeSkillId`. Только на чтение. */
  hitEffectChargeSkill?: GameDataRefDto | null;
  hitEffectChargeSkillLevel?: number | null;
  markType?: number | null;
  skillEffect?: string | null;
  chatBackground?: string | null;
  limitedIcon?: string | null;
  /**
   * Имя иконки для сайта, выведенное бэком из `limitedIcon`: часть после последней точки,
   * в нижнем регистре — как у иконок вики. Только на чтение: в запросы не уходит и в мёрдже
   * не сравнивается (производное от `limitedIcon`). `null`, если `limitedIcon` пустой.
   */
  limitedIconName?: string | null;
  activationCost?: OrnamentCostDto[];
  deactivationCost?: OrnamentCostDto[];
  activatableItems?: OrnamentActivatableItemDto[];
  createdAt: string;
  updatedAt: string;
  updatedById?: number | null;
  updatedByUsername?: string | null;
}

/** Категория стилей вместе с набором применимых полей. */
export interface OrnamentCategoryDto {
  /** Версия строки категории — обязательна в PUT `/categories/{code}`. */
  version: number;
  /** ID категории в игре. */
  categoryId: number;
  code: OrnamentCategoryCode;
  /** Подпись на языке запроса. */
  title: string;
  swapCostItemId?: number | null;
  /** Расшифровка `swapCostItemId`. Только на чтение. */
  swapCostItem?: GameDataRefDto | null;
  swapCostCount?: number | null;
  styleCount: number;
  /** Поле, из которого берётся визуал стиля. */
  visualField?: OrnamentField | null;
  /** Поля, применимые к этой категории: остальные UI показывать не должен. */
  fields: OrnamentField[];
  updatedById?: number | null;
  updatedByUsername?: string | null;
}

/** Тело POST `/` — создание стиля. */
export interface CreateOrnamentDto {
  category: OrnamentCategoryCode;
  /** Не указан — бэк возьмёт следующий свободный номер. */
  styleId?: number;
  name: string;
  swapItemId?: number;
  npcId?: number;
  transformationId?: number;
  skillId?: number;
  hitEffectChargeSkillId?: number;
  hitEffectChargeSkillLevel?: number;
  markType?: number;
  skillEffect?: string;
  chatBackground?: string;
  limitedIcon?: string;
  activationCost?: OrnamentCostDto[];
  deactivationCost?: OrnamentCostDto[];
  activatableItems?: OrnamentActivatableItemDto[];
}

/**
 * Тело PUT `/{id}` — ПОЛНАЯ замена: не переданные поля очищаются.
 * `version` обязателен, без него бэк ответит 400.
 */
export interface UpdateOrnamentDto extends CreateOrnamentDto {
  version: number;
  styleId: number;
}

/** Тело PUT `/categories/{code}` — цена смены стиля внутри категории. */
export interface UpdateOrnamentCategoryDto {
  version: number;
  swapCostItemId?: number;
  swapCostCount?: number;
}

/** Строка для массовой правки: что меняем и какую версию читали. */
export interface OrnamentRowVersionDto {
  id: number;
  version: number;
}

/**
 * Тело POST `/bulk`. Применяются ТОЛЬКО переданные поля.
 * Категорию и `styleId` массово менять нельзя.
 */
export interface BulkUpdateOrnamentsDto {
  rows: OrnamentRowVersionDto[];
  markType?: number;
  skillEffect?: string;
  chatBackground?: string;
  limitedIcon?: string;
  /** Заменяет цену активации целиком. */
  activationCost?: OrnamentCostDto[];
  /** Заменяет цену деактивации целиком. */
  deactivationCost?: OrnamentCostDto[];
  /** Заменяет ограничения по расе, полу и классу целиком. */
  activatableItems?: OrnamentActivatableItemDto[];
  /** Поля, которые нужно очистить у всех выбранных стилей. */
  clear?: OrnamentClearField[];
}

export interface BulkUpdateOrnamentsResultDto {
  /** Стили, к которым изменения применены. */
  updated: OrnamentDto[];
  /** Изменённые кем-то другим за это время: НЕ применены, здесь их актуальные значения. */
  conflicted: OrnamentDto[];
}

export interface BulkDeleteOrnamentsDto {
  ids: number[];
}

export interface PaginatedOrnamentsDto {
  content: OrnamentDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

/** Параметры GET `/`. */
/**
 * Фильтры по колонкам списка стилей. `name` и текстуры — по куску текста, остальное — точно.
 * `activationCost`/`deactivationCost` повторяются в формате ингредиентов алхимии («57», «57:10», «:10»).
 * `restriction` повторяется: «раса:пол:класс», пустая часть — любое значение («1:0:», «2::»); каждое
 * условие проверяется на одной строке ограничений (бывшие `raceId`/`sexId`/`classId`). Бывший
 * `search` — это `name`.
 * Категория — отдельный фильтр (`category`): от неё зависит набор колонок.
 */
export const ORNAMENT_FILTER_PARAMS = [
  'id',
  'styleId',
  'name',
  'swapItemId',
  'npcId',
  'transformationId',
  'skillId',
  'hitEffectChargeSkillId',
  'hitEffectChargeSkillLevel',
  'markType',
  'skillEffect',
  'chatBackground',
  'limitedIcon',
  'activationCost',
  'deactivationCost',
  'restriction',
] as const;

export interface OrnamentsFilters {
  category?: OrnamentCategoryCode | '';
  /** Фильтры по колонкам (`ORNAMENT_FILTER_PARAMS`). */
  columns?: ColumnFilterValues;
  sort?: OrnamentSortOption;
  page?: number;
  size?: number;
}

/**
 * `markType` — плашка «выгодная сделка» на стиле в клиенте игры: 0 — нет, 1 — показывать.
 *
 * Бэк значение не расшифровывает (для него это просто число ≥ 0), смысл известен со слов
 * владельца (10.09.2026). Других чисел клиент не знает; если такое попадётся в данных, фронт
 * покажет его как есть и не перепишет молча — только явным переключением.
 */
export const MARK_TYPE = { OFF: 0, ON: 1 } as const;

/** Ограничения из Swagger — держим рядом, чтобы валидация формы не расходилась с бэком. */
export const ORNAMENT_LIMITS = {
  NAME_MAX: 255,
  TEXTURE_MAX: 255,
  COST_MAX_ROWS: 20,
  ACTIVATABLE_MAX_ROWS: 200,
  BULK_MAX_ROWS: 500,
  /** `count`, `swapCostCount`, `raceId`, `sexId`, `classId` — short на бэке. */
  SHORT_MAX: 32767,
} as const;
